<script lang="ts">
  import { onMount } from 'svelte';
  import scriptLoadingHelper from '$lib/helpers/script-loading-helper';
  import VideoPlayerModalPartial from '../ModalPartials/VideoPlayerModalPartial.svelte';
  import { VideoUrlDetector } from './video-url-detector';

  export let videoUrl: string;
  export let title: string | undefined = '';

  const WATCHED_PERCENT_TIERS = [25, 50, 75, 100];
  const YOUTUBE_API_SRC = 'https://www.youtube.com/iframe_api';
  const VIMEO_API_SRC = 'https://player.vimeo.com/api/player.js';

  const isYouTubePlaylist = VideoUrlDetector.isYouTubeList(videoUrl);
  const isYouTube = VideoUrlDetector.isYouTube(videoUrl);
  const isVimeo = VideoUrlDetector.isVimeo(videoUrl);

  const modalPartialConfig: ModalPartialConfig = {
    type: 'videoPlayer',
    active: true,
    size: 'fullscreen',
    title: '',
  };

  let isModalOpen = false;
  let vimeoTime = 0;
  let vimeoDuration = 0;
  let youtubePlayer: YT.Player | null = null;
  let vimeoPlayer: Vimeo.Player | null = null;
  let videoElement: HTMLElement;
  // video progress timer
  let progressLoop: ReturnType<typeof setInterval> | undefined;
  // saved progress for current video
  let currentVideoWatchedTier: number = 0;

  // get the video url of the currently loaded video for tracking purposes
  function getCurrentVideoUrl() {
    if (isYouTube || isVimeo) {
      return videoUrl;
    } else if (isYouTubePlaylist && youtubePlayer) {
      return youtubePlayer.getVideoUrl();
    }
    return `Unknown video from: ${window.location.href}`;
  }

  // handle the modal closing
  function handleModalClose() {
    if (isModalOpen) {
      if (youtubePlayer) {
        youtubePlayer.pauseVideo();
      }
      if (vimeoPlayer) {
        vimeoPlayer.pause();
      }
      // stop progress loop
      if (progressLoop) {
        clearInterval(progressLoop);
      }
      // track video progress when closed
      window.Sq.Tracking.track(
        {
          category: 'Video',
          action: 'End',
          properties: { url: getCurrentVideoUrl(), percentWatched: computePercentageWatched() },
        },
        { destinations: ['googleAnalytics', 'optimizelyWeb'] }
      );
      // close modal
      isModalOpen = false;

      // re-enable scrolling since modal was not manually closed
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }

  // get the percentage of current video watched 0-100
  function computePercentageWatched() {
    let percent = 0;
    // for youtube
    if ((isYouTube || isYouTubePlaylist) && youtubePlayer) {
      percent = youtubePlayer.getCurrentTime() / youtubePlayer.getDuration();
    }
    // for vimeo
    if (isVimeo && vimeoPlayer) {
      vimeoPlayerSetCurrentTime();
      percent = vimeoTime / vimeoDuration;
    }
    return Math.round(percent * 100);
  }

  // handle video progress
  function onProgress() {
    const currentProgressPercent = computePercentageWatched();
    // loop through each progress tier
    WATCHED_PERCENT_TIERS.forEach((tier) => {
      // check if current progress hits a new tier
      if (currentProgressPercent >= tier && currentVideoWatchedTier < tier) {
        // update watched progress tier for current video
        currentVideoWatchedTier = tier;
        // track the progress event
        window.Sq.Tracking.track(
          { category: 'Video', action: 'Progress', properties: { url: getCurrentVideoUrl(), percentWatched: tier } },
          { destinations: ['googleAnalytics', 'optimizelyWeb'] }
        );
      }
    });
  }

  // handle a video starting
  function trackStart() {
    // reset amout watched when a video starts
    currentVideoWatchedTier = 0;
    // track the video start
    window.Sq.Tracking.track(
      { category: 'Video', action: 'Start', properties: { url: getCurrentVideoUrl() } },
      { destinations: ['googleAnalytics', 'optimizelyWeb'] }
    );
    // clear the progress loop
    if (progressLoop) {
      clearInterval(progressLoop);
    }
    // start a fresh progress loop every second
    progressLoop = setInterval(onProgress, 1000);
  }

  function vimeoPlayerSetCurrentTime() {
    vimeoPlayer?.getCurrentTime().then((curTime: number) => {
      vimeoTime = curTime;
    });
  }

  function initVimeoPlayer(vimeoId: string) {
    const onVimeoIframeReady = () => {
      if (vimeoPlayer === null) {
        vimeoPlayer = new Vimeo.Player(videoElement, {
          id: vimeoId,
          autoplay: true,
        });
      }
      vimeoPlayer.play();
      vimeoPlayer.on('ended', () => {
        handleModalClose();
      });
      vimeoPlayer.getDuration().then((curDuration: number) => {
        vimeoDuration = curDuration;
      });
    };

    if (window.Vimeo && window.Vimeo.Player) {
      onVimeoIframeReady();
    } else {
      window.onVimeoIframeReady = onVimeoIframeReady;
    }
  }

  function onYouTubeStateChange(e: { data: number }) {
    if (isYouTubePlaylist && e.data === -1) {
      // track playlist video starts when they are loaded and about to autoplay
      trackStart();
    } else if (!isYouTubePlaylist && e.data === YT.PlayerState.ENDED) {
      // auto-close modals when single videos end
      handleModalClose();
    }
  }

  function initYouTubePlayer(
    youtubeId: string,
    youtubePlaylistId: string | undefined = undefined, // undefined for single videos
    youtubePlaylistIndex: number | undefined = undefined
  ) {
    const playlistConfig = { list: youtubePlaylistId, listType: 'playlist', index: youtubePlaylistIndex };
    const onYouTubeIframeAPIReady = () => {
      // create the player if it doesn't exist
      if (!youtubePlayer) {
        youtubePlayer = new YT.Player(videoElement, {
          videoId: youtubePlaylistId ? undefined : youtubeId, // for single videos, initialize the player with the video id
          playerVars: {
            autoplay: 1,
            color: 'white',
            fs: 0,
            modestbranding: 1,
            rel: 0,
            showinfo: 0,
          },
          events: {
            onReady: () => {
              // for playlists, wait for the player to be ready before loading the playlist
              if (youtubePlaylistId) {
                // @ts-expect-error playlistConfig
                youtubePlayer?.loadPlaylist(playlistConfig);
              }
            },
            onStateChange: onYouTubeStateChange,
          },
        });
      } else if (youtubeId && !youtubePlaylistId) {
        youtubePlayer.loadVideoById(youtubeId);
      } else if (youtubePlaylistId) {
        // @ts-expect-error playlistConfig
        youtubePlayer.loadPlaylist(playlistConfig);
      }
    };

    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      window.onYouTubeIframeAPIReady = onYouTubeIframeAPIReady;
    }
  }

  // initialize either the youtube or vimeo video player
  function initPlayer() {
    // check for a youtube playlist
    if (isYouTubePlaylist) {
      // get the video id, list id, and video index from the url
      const youtubeListData = VideoUrlDetector.getYoutubeListData(videoUrl);
      if (youtubeListData) {
        initYouTubePlayer(youtubeListData.videoId, youtubeListData.listId, youtubeListData.index);
      }
      // check for a single youtube video
    } else if (isYouTube) {
      const youtubeId = VideoUrlDetector.getYoutubeId(videoUrl);
      if (youtubeId) {
        initYouTubePlayer(youtubeId);
      }
      // check for a single vimeo video
    } else if (isVimeo) {
      const vimeoId = VideoUrlDetector.getVimeoId(videoUrl);
      if (vimeoId) {
        initVimeoPlayer(vimeoId);
      }
    }
  }

  const handleVideoClick = (event: Event) => {
    event.preventDefault();
    initPlayer();
    // single videos track their starts when modals are opened (playlists start when they are loaded)
    if (!isYouTubePlaylist) {
      trackStart();
    }
    isModalOpen = !isModalOpen;
  };

  onMount(async () => {
    if (!window.YT) {
      scriptLoadingHelper.injectClientScript('youtube-iframe-api-script-tag', YOUTUBE_API_SRC, 'afterInteractive', {
        async: true,
      });
    }
    if (isVimeo && !window.Vimeo) {
      scriptLoadingHelper.injectClientScript('vimeo-api-script-tag', VIMEO_API_SRC, 'afterInteractive', {
        async: true,
      });
    }
  });
</script>

<slot {handleVideoClick}>
  <a class="video-player-link" href={videoUrl} on:click={handleVideoClick} {title}>
    <slot name="video-player-link-content" />
  </a>
</slot>

<VideoPlayerModalPartial bind:videoElement {handleModalClose} {modalPartialConfig} {isModalOpen} />
